<template>
  <div class="topo-wrap">
    <div class="topo-toolbar">
      <div class="topo-toolbar__group">
        <select
          v-model="gwId"
          class="topo-field topo-field--select"
        >
          <option
            v-for="g in gateways"
            :key="g.gwId"
            :value="g.gwId"
          >
            {{ g.locationName ?? `${$t('components.infrastructure.gateways.unassigned')} · ${g.gwId}` }}{{ g.state === 'online' ? '' : ` · ${$t('components.infrastructure.topology.detail.offline')}` }}
          </option>
        </select>
        <span class="topo-search">
          <i class="bi bi-search topo-search__icon" />
          <input
            v-model="q"
            class="topo-field"
            :placeholder="$t('components.infrastructure.topology.searchPlaceholder')"
          >
          <button
            v-if="q"
            type="button"
            class="topo-search__clear"
            @click="q = ''"
          >
            <i class="bi bi-x-lg" />
          </button>
        </span>
        <span
          v-if="q"
          class="topo-search__count"
        >{{ matchSet.size }} {{ $t('components.infrastructure.topology.matches') }}</span>
      </div>
      <div class="topo-toolbar__group">
        <span class="topo-live-badge">
          <span class="wirepas-status-dot wirepas-status-dot--online" />{{ $t('components.infrastructure.topology.live') }}
        </span>
        <div
          class="topo-mode"
          role="group"
        >
          <span
            class="topo-mode__thumb"
            :class="{ 'topo-mode__thumb--right': mode === 'hierarchical' }"
          />
          <button
            type="button"
            class="topo-mode__btn"
            :class="{ 'topo-mode__btn--active': mode === 'organic' }"
            @click="mode = 'organic'"
          >
            {{ $t('components.infrastructure.topology.modeOrganic') }}
          </button>
          <button
            type="button"
            class="topo-mode__btn"
            :class="{ 'topo-mode__btn--active': mode === 'hierarchical' }"
            @click="mode = 'hierarchical'"
          >
            {{ $t('components.infrastructure.topology.modeHierarchical') }}
          </button>
        </div>
        <div class="topo-legend">
          <span class="topo-legend__item"><span class="topo-legend__swatch topo-legend__swatch--rect" />{{ $t('components.infrastructure.topology.legend.gateway') }}</span>
          <span class="topo-legend__item"><span class="topo-legend__swatch topo-legend__swatch--diamond" />{{ $t('components.infrastructure.topology.legend.anchor') }}</span>
          <span class="topo-legend__item"><span class="topo-legend__swatch topo-legend__swatch--circle" />{{ $t('components.infrastructure.topology.legend.tag') }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="error"
      class="alert alert-danger d-flex align-items-center gap-2 m-0 mb-2"
    >
      <i class="bi bi-exclamation-triangle-fill" />
      <span>{{ error }}</span>
    </div>

    <div
      ref="panelEl"
      class="topo-panel"
    >
      <div
        v-if="empty"
        class="topo-empty"
      >
        {{ loading ? $t('components.infrastructure.topology.loading') : $t('components.infrastructure.topology.empty') }}
      </div>
      <template v-else>
        <div class="topo-controls">
          <button
            type="button"
            class="topo-zoom-btn"
            :title="$t('components.infrastructure.topology.zoomIn')"
            @click="zoomBy(1.3)"
          >
            +
          </button>
          <button
            type="button"
            class="topo-zoom-btn"
            :title="$t('components.infrastructure.topology.zoomOut')"
            @click="zoomBy(1 / 1.3)"
          >
            −
          </button>
          <button
            type="button"
            class="topo-zoom-btn"
            :title="$t('components.infrastructure.topology.resetView')"
            @click="resetView"
          >
            <i class="bi bi-compass" />
          </button>
        </div>
        <div class="topo-hint">
          {{ $t('components.infrastructure.topology.hint') }}
        </div>

        <div
          v-if="departedTags.length || overflowTags.length"
          class="topo-departed"
        >
          <template v-if="overflowTags.length">
            <button
              type="button"
              class="topo-departed__head"
              :aria-expanded="overflowExpanded"
              @click="overflowExpanded = !overflowExpanded"
            >
              <i
                class="bi bi-chevron-right topo-departed__chevron"
                :class="{ 'topo-departed__chevron--open': overflowExpanded }"
              />
              <span>{{ $t('components.infrastructure.topology.parking.graphOverflow') }} ({{ overflowTags.length }})</span>
            </button>
            <table
              v-if="overflowExpanded"
              class="topo-departed__table"
            >
              <thead>
                <tr>
                  <th>{{ $t('components.infrastructure.topology.detail.address') }}</th>
                  <th>{{ $t('components.infrastructure.topology.detail.battery') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="n in overflowTags"
                  :key="n.id"
                >
                  <td>
                    <router-link
                      v-if="topoLink(n)"
                      :to="topoLink(n)!"
                    >
                      {{ n.label }}
                    </router-link>
                    <span v-else>{{ n.label }}</span>
                  </td>
                  <td>{{ n.battery_pct != null ? `${n.battery_pct}%` : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </template>

          <template v-if="departedTags.length">
            <button
              type="button"
              class="topo-departed__head"
              :aria-expanded="departedExpanded"
              @click="departedExpanded = !departedExpanded"
            >
              <i
                class="bi bi-chevron-right topo-departed__chevron"
                :class="{ 'topo-departed__chevron--open': departedExpanded }"
              />
              <span>{{ $t('components.infrastructure.topology.parking.unreachable') }} ({{ departedTags.length }})</span>
            </button>
            <table
              v-if="departedExpanded"
              class="topo-departed__table"
            >
              <thead>
                <tr>
                  <th>{{ $t('components.infrastructure.topology.detail.address') }}</th>
                  <th>{{ $t('components.infrastructure.topology.detail.battery') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="n in departedTags"
                  :key="n.id"
                >
                  <td>
                    <router-link
                      v-if="topoLink(n)"
                      :to="topoLink(n)!"
                    >
                      {{ n.label }}
                    </router-link>
                    <span v-else>{{ n.label }}</span>
                  </td>
                  <td>{{ n.battery_pct != null ? `${n.battery_pct}%` : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>

        <div
          v-if="selNode"
          class="topo-detail"
        >
          <div class="topo-detail__head">
            <span class="topo-detail__title">
              <span
                class="topo-detail__dot"
                :class="{ 'topo-detail__dot--round': selNode.kind === 'tag' }"
                :style="{ background: nodeFill(selNode) }"
              />
              <router-link
                v-if="topoLink(selNode)"
                :to="topoLink(selNode)!"
              >
                {{ selNode.label }}
              </router-link>
              <strong v-else>{{ selNode.label }}</strong>
            </span>
            <button
              type="button"
              class="topo-detail__close"
              @click="sel = null"
            >
              <i class="bi bi-x-lg" />
            </button>
          </div>
          <div class="topo-detail__body">
            <div class="topo-detail__row">
              <span>{{ $t('components.infrastructure.topology.detail.nodeType') }}</span>
              <span>{{ nodeKindLabel(selNode.kind) }}</span>
            </div>
            <template v-if="selNode.kind === 'gateway'">
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.gwAddress') }}</span>
                <router-link
                  v-if="topoLink(selNode)"
                  class="mono"
                  :to="topoLink(selNode)!"
                >
                  {{ selNode.id.replace(/^gw:/, '') }}
                </router-link>
                <span
                  v-else
                  class="mono"
                >{{ selNode.id.replace(/^gw:/, '') }}</span>
              </div>
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.feature') }}</span>
                <span>{{ selNode.type ?? $t('components.infrastructure.topology.detail.noType') }}</span>
              </div>
            </template>
            <div
              v-if="selNode.kind === 'anchor'"
              class="topo-detail__row"
            >
              <span>{{ $t('components.infrastructure.topology.detail.address') }}</span>
              <span class="mono">{{ selNode.id.replace(/^an:/, '') }}</span>
            </div>
            <template v-if="selNode.kind === 'tag'">
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.address') }}</span>
                <span class="mono">{{ selNode.addr ?? '—' }}</span>
              </div>
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.status') }}</span>
                <span>{{ selNode.status ? $t(`components.wirepasAssets.status.${selNode.status}`) : '—' }}</span>
              </div>
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.hops') }}</span>
                <span>{{ selNode.hops != null ? selNode.hops : '—' }}</span>
              </div>
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.travelTime') }}</span>
                <span>{{ selNode.travel_ms != null ? `${selNode.travel_ms} ms` : '—' }}</span>
              </div>
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.bestRssi') }}</span>
                <span>{{ selNode.rssi != null ? `${selNode.rssi} dBm` : '—' }}</span>
              </div>
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.battery') }}</span>
                <span>{{ selNode.battery_pct != null ? `${selNode.battery_pct}%` : '—' }}</span>
              </div>
            </template>
            <template v-if="selNode.kind === 'anchor'">
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.rssi') }}</span>
                <span>{{ selNode.rssi != null ? `${selNode.rssi} dBm` : '—' }}</span>
              </div>
              <div class="topo-detail__row">
                <span>{{ $t('components.infrastructure.topology.detail.nextHopRssi') }}</span>
                <span>{{ selNode.nextHopRssiDbm != null ? `${selNode.nextHopRssiDbm} dBm` : '—' }}</span>
              </div>
              <div
                v-if="selNode.nextHopAddress != null"
                class="topo-detail__row"
              >
                <span>{{ $t('components.infrastructure.topology.detail.nextHop') }}</span>
                <span class="mono">{{ selNode.nextHopAddress }}</span>
              </div>
            </template>
            <div
              v-if="selNode.kind !== 'tag'"
              class="topo-detail__row"
            >
              <span>{{ $t('components.infrastructure.topology.detail.state') }}</span>
              <span>{{ selNode.active === false ? $t('components.infrastructure.topology.detail.offline') : $t('components.infrastructure.topology.detail.online') }}</span>
            </div>
          </div>
        </div>

        <svg
          ref="svgEl"
          class="topo-svg"
          :viewBox="`0 0 ${dims.w} ${dims.h}`"
          preserve-aspect-ratio="xMidYMid meet"
          role="img"
          :aria-label="$t('components.infrastructure.topology.hint')"
          @pointerdown="onSvgDown"
          @pointermove="onMove"
          @pointerup="onUp"
        >
          <g :transform="`translate(${zoom.x},${zoom.y}) scale(${zoom.k})`">
            <text
              v-if="parkLabels.anchors"
              :x="parkLabels.anchors.x"
              :y="parkLabels.anchors.y"
              text-anchor="start"
              dominant-baseline="central"
              :fill="C_DIM"
              font-size="11"
            >{{ $t('components.infrastructure.topology.parking.offlineAnchors') }} ({{ offAnchorCount }}) →</text>

            <g
              v-for="(l, i) in renderLinks"
              :key="`l${i}`"
              :opacity="edgeDimmed(l) ? 0.06 : 1"
            >
              <line
                v-if="linkNode(l.source) && linkNode(l.target)"
                class="topo-flow"
                :x1="linkNode(l.source)!.x"
                :y1="linkNode(l.source)!.y"
                :x2="linkNode(l.target)!.x"
                :y2="linkNode(l.target)!.y"
                :stroke="edgeColor(l)"
                :stroke-width="linkId(l.source).startsWith('tag:') ? 1.5 : 1.1"
                vector-effect="non-scaling-stroke"
              />
              <text
                v-if="l.rssi != null && linkNode(l.source) && linkNode(l.target)"
                :x="(linkNode(l.source)!.x! + linkNode(l.target)!.x!) / 2"
                :y="(linkNode(l.source)!.y! + linkNode(l.target)!.y!) / 2 - 3"
                text-anchor="middle"
                :fill="C_DIM"
                font-size="10"
              >{{ l.rssi }} dBm</text>
            </g>

            <g
              v-for="n in renderNodes"
              :key="n.id"
              class="topo-node"
              :transform="`translate(${n.x},${n.y})`"
              :opacity="nodeDimmed(n) ? 0.12 : 1"
              @pointerdown="onNodeDown($event, n)"
            >
              <template v-if="n.kind === 'gateway'">
                <rect
                  v-if="n.id === sel"
                  :x="-radiusOf(n) - 5"
                  :y="-radiusOf(n) - 5"
                  :width="(radiusOf(n) + 5) * 2"
                  :height="(radiusOf(n) + 5) * 2"
                  rx="14"
                  fill="none"
                  stroke="#58a6ff"
                  stroke-width="1.5"
                />
                <rect
                  :x="-radiusOf(n)"
                  :y="-radiusOf(n)"
                  :width="radiusOf(n) * 2"
                  :height="radiusOf(n) * 2"
                  rx="11"
                  :fill="C_GW"
                  :stroke="n.active === false ? C_OFF : '#58a6ff'"
                  stroke-width="2"
                />
                <text
                  text-anchor="middle"
                  :y="radiusOf(n) + 16"
                  :fill="C_TEXT"
                  font-size="12"
                  font-weight="600"
                >{{ n.label }}</text>
              </template>
              <template v-else-if="n.kind === 'anchor'">
                <rect
                  v-if="n.id === sel"
                  :x="-radiusOf(n) - 5"
                  :y="-radiusOf(n) - 5"
                  :width="(radiusOf(n) + 5) * 2"
                  :height="(radiusOf(n) + 5) * 2"
                  transform="rotate(45)"
                  fill="none"
                  stroke="#58a6ff"
                  stroke-width="1.5"
                />
                <rect
                  :x="-radiusOf(n)"
                  :y="-radiusOf(n)"
                  :width="radiusOf(n) * 2"
                  :height="radiusOf(n) * 2"
                  rx="3"
                  transform="rotate(45)"
                  :fill="C_ANCHOR"
                  :stroke="n.active === false ? C_OFF : C_ANCHOR_BORDER"
                  stroke-width="2"
                />
                <text
                  text-anchor="middle"
                  :y="radiusOf(n) * 1.41 + 14"
                  :fill="C_TEXT"
                  font-size="11"
                >{{ n.label }}</text>
              </template>
              <template v-else>
                <circle
                  v-if="n.id === sel"
                  :r="radiusOf(n) + 6"
                  fill="none"
                  stroke="#58a6ff"
                  stroke-width="1.5"
                />
                <circle
                  :r="radiusOf(n)"
                  :fill="nodeFill(n)"
                  :stroke="CANVAS_BG"
                  stroke-width="2.5"
                />
                <text
                  text-anchor="middle"
                  :y="radiusOf(n) + 15"
                  :fill="C_TEXT"
                  font-size="11"
                >{{ n.label }}</text>
                <g
                  v-if="n.hops != null"
                  :transform="`translate(${radiusOf(n) - 2},${-radiusOf(n) - 2})`"
                >
                  <circle
                    r="9"
                    fill="#161b22"
                    :stroke="nodeFill(n)"
                    stroke-width="1.5"
                  />
                  <text
                    text-anchor="middle"
                    dominant-baseline="central"
                    :fill="C_TEXT"
                    font-size="10"
                    font-weight="600"
                  >{{ n.hops }}</text>
                </g>
              </template>
            </g>
          </g>
        </svg>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, PropType } from 'vue'
import { useWirepasTopologyGraph } from '@/composables/useWirepasTopologyGraph'
import {
  radiusOf, nodeFill, linkId,
  CANVAS_BG, C_GRID, C_GW, C_ANCHOR, C_ANCHOR_BORDER, C_TEXT, C_DIM, C_EDGE, C_OFF,
} from '@/utils/wirepas-topology'

// This component is intentionally a thin view: all fetching/polling, the d3-force simulation
// engine and the pan/zoom/drag interaction live in composables (useWirepasTopologyGraph /
// useGraphPanZoom) so they're testable and reusable independent of this template.
export default defineComponent({
  name: 'WirepasTopologyGraph',
  props: {
    // Whether this tab is shown -- gates fetching/polling.
    active: { type: Boolean as PropType<boolean>, default: false },
  },
  setup(props) {
    const panelEl = ref<HTMLDivElement | null>(null)
    const svgEl = ref<SVGSVGElement | null>(null)
    const graph = useWirepasTopologyGraph(props, panelEl, svgEl)
    const departedExpanded = ref(false)
    const overflowExpanded = ref(false)

    return {
      panelEl, svgEl, departedExpanded, overflowExpanded,
      radiusOf, nodeFill, linkId,
      CANVAS_BG, C_GRID, C_GW, C_ANCHOR, C_ANCHOR_BORDER, C_TEXT, C_DIM, C_EDGE, C_OFF,
      ...graph,
    }
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-topology-view.scss';

// v-bind() must be written in this file's own <style> block: Vue's compiler only scans a
// component's inline <style> source for v-bind() calls, not content pulled in via @import,
// so these colors silently never got their CSS custom properties set from the shared file.
.topo-legend__swatch--rect { background: v-bind(C_GW); }
.topo-legend__swatch--diamond { background: v-bind(C_ANCHOR_BORDER); }
.topo-panel {
  background-color: v-bind(CANVAS_BG);
  background-image: radial-gradient(v-bind(C_GRID) 1px, transparent 1px);
}
.topo-empty { color: v-bind(C_DIM); }
.topo-hint { color: v-bind(C_DIM); }
</style>
